import { onMounted, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import useContent from '../stores/useContent';
import useSuspend from './useSuspend';
import { findScreenById } from './applyPatch';
import useAudioNarration, { isJitAudioBusy } from './useAudioNarration';
import {
  resolveJitAllSlotTargets,
  resolveJitEntryTargets,
  type JitPrefetchTarget
} from './jitEntryPrefetch';
import type { NarratableScreen } from './screenAudioText';
import { jitAudioLog } from './jitAudioLog';

const IDLE_TIMEOUT_MS = 2000;
const AUDIO_PLAYING_RETRY_MS = 1500;

function scheduleIdleTask(callback: () => void): number {
  if (typeof requestIdleCallback === 'function') {
    return requestIdleCallback(callback, {
      timeout: IDLE_TIMEOUT_MS
    }) as number;
  }
  return window.setTimeout(callback, 100);
}

function cancelIdleTask(id: number): void {
  if (typeof cancelIdleCallback === 'function') {
    cancelIdleCallback(id);
  } else {
    clearTimeout(id);
  }
}

function shouldDeferPrefetch(): boolean {
  if (typeof document !== 'undefined' && document.hidden) return true;
  const conn = (
    navigator as Navigator & { connection?: { saveData?: boolean } }
  ).connection;
  if (conn?.saveData) return true;
  return false;
}

/** Next/prev visible section in the same scroll page (matches uc-scroll-page findNeighbor). */
export function findScrollSectionNeighbor(
  currentEl: HTMLElement,
  direction: 'next' | 'prev'
): HTMLElement | null {
  const container = currentEl.closest('.scroll-page__content');
  if (!container) return null;

  const sections = [
    ...container.getElementsByClassName('section')
  ] as HTMLElement[];
  const i = sections.indexOf(currentEl);
  if (i < 0) return null;

  if (direction === 'next') {
    return (
      sections
        .slice(i + 1)
        .find(s => s.getAttribute('data-section-hidden') !== 'true') ?? null
    );
  }

  return (
    sections
      .slice(0, i)
      .reverse()
      .find(s => s.getAttribute('data-section-hidden') !== 'true') ?? null
  );
}

/**
 * Two-phase JiT cache prefetch on each section enter (idle, sequential):
 * 1. All narration slots for the current screen (feedback, every tab, etc.)
 * 2. On-enter sequence for the next visible scroll section (intro + tab-0, …)
 *
 * Call once from the player shell ({@link AppContent.vue}).
 */
export default function useJitAudioLookahead(options?: {
  enabled?: () => boolean;
}) {
  const contentStore = useContent();
  const { compiled } = storeToRefs(contentStore);
  const suspend = useSuspend();
  const { audioAiJitEnabled, resolveText, warmNarrationCache } =
    useAudioNarration();

  let idleId: number | null = null;
  let abortController: AbortController | null = null;
  const cancelScheduled = () => {
    if (idleId !== null) {
      cancelIdleTask(idleId);
      idleId = null;
    }
    if (abortController) {
      jitAudioLog('prefetch cancelled');
    }
    abortController?.abort();
    abortController = null;
  };

  const prefetchCtx = () => ({
    compiled: compiled.value,
    locale: suspend?.locale ?? 'en-GB',
    resolveText
  });

  const warmTargetList = async (
    targets: JitPrefetchTarget[],
    signal: AbortSignal,
    phaseLabel: string
  ) => {
    if (targets.length === 0) {
      jitAudioLog(`prefetch ${phaseLabel}: no slots`);
      return;
    }

    jitAudioLog(`prefetch ${phaseLabel}: ${targets.length} clip(s)`);

    for (const target of targets) {
      if (signal.aborted) return;
      if (shouldDeferPrefetch()) return;
      if (isJitAudioBusy()) {
        jitAudioLog(`prefetch ${phaseLabel}: waiting (JiT busy)`);
        await new Promise<void>(resolve => {
          const retry = () => {
            if (signal.aborted) {
              resolve();
              return;
            }
            if (!isJitAudioBusy() && !suspend?.audioplaying) {
              resolve();
              return;
            }
            window.setTimeout(retry, AUDIO_PLAYING_RETRY_MS);
          };
          retry();
        });
      }
      if (signal.aborted || suspend?.audioplaying) return;

      try {
        await warmNarrationCache(target.text, `prefetch:${target.key}`);
      } catch (error) {
        console.warn('JiT lookahead cache warm failed', target.key, error);
      }
    }
  };

  const runTwoPhasePrefetch = async (
    currentScreen: NarratableScreen | undefined,
    nextScreen: NarratableScreen | undefined,
    signal: AbortSignal
  ) => {
    const ctx = prefetchCtx();

    if (signal.aborted) return;

    if (currentScreen?.id) {
      jitAudioLog(
        'prefetch phase 1: current screen all slots',
        currentScreen.id
      );
      await warmTargetList(
        resolveJitAllSlotTargets(currentScreen, ctx),
        signal,
        `current:${currentScreen.id}`
      );
    }

    if (signal.aborted) return;

    if (!nextScreen?.id) {
      jitAudioLog('prefetch phase 2: skipped (no next section)');
      return;
    }

    jitAudioLog('prefetch phase 2: next screen on-enter', nextScreen.id);
    await warmTargetList(
      resolveJitEntryTargets(nextScreen, ctx),
      signal,
      `next-entry:${nextScreen.id}`
    );
    if (!signal.aborted) {
      jitAudioLog('prefetch done', {
        current: currentScreen?.id,
        next: nextScreen.id
      });
    }
  };

  const scheduleLookahead = (currentSection: HTMLElement) => {
    cancelScheduled();

    const currentScreen = findScreenById(compiled.value, currentSection.id) as
      | NarratableScreen
      | undefined;

    const neighbor = findScrollSectionNeighbor(currentSection, 'next');
    const nextScreen = neighbor?.id
      ? (findScreenById(compiled.value, neighbor.id) as
          | NarratableScreen
          | undefined)
      : undefined;

    if (!currentScreen && !nextScreen) return;

    const ac = new AbortController();
    abortController = ac;

    const startPrefetch = () => {
      if (ac.signal.aborted) return;
      if (!audioAiJitEnabled.value) return;
      if (options?.enabled && !options.enabled()) return;
      if (shouldDeferPrefetch()) return;
      jitAudioLog('prefetch starting (idle)', {
        current: currentScreen?.id,
        next: nextScreen?.id
      });
      void runTwoPhasePrefetch(currentScreen, nextScreen, ac.signal);
    };

    idleId = scheduleIdleTask(() => {
      idleId = null;
      if (suspend?.audioplaying) {
        jitAudioLog('prefetch deferred (audio playing)');
        idleId = scheduleIdleTask(() => {
          idleId = null;
          if (!ac.signal.aborted && !suspend?.audioplaying) {
            startPrefetch();
          }
        }) as number;
        return;
      }
      startPrefetch();
    });
  };

  const onScrollSectionEntered = (event: Event) => {
    if (!audioAiJitEnabled.value) return;
    if (options?.enabled && !options.enabled()) return;

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.classList.contains('section')) return;

    scheduleLookahead(target);
  };

  onMounted(() => {
    document.addEventListener(
      'scrollsectionentered',
      onScrollSectionEntered,
      true
    );
  });

  onUnmounted(() => {
    document.removeEventListener(
      'scrollsectionentered',
      onScrollSectionEntered,
      true
    );
    cancelScheduled();
  });
}
